import { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import { Checkbox } from './checkbox';
import { Label } from './label';
import clsx from 'clsx';
import { type LabelValue } from '@/types/global';

interface ControllerCheckboxGroupProps<FieldsType extends FieldValues> {
  field: ControllerRenderProps<FieldsType, Path<FieldsType>>;
  options: Array<LabelValue<string>>;
  isViewMode?: boolean;
  displayInColumns?: boolean;
  displayAllOption?: boolean;
}

const ControllerCheckboxGroup = <FieldsType extends FieldValues>({
  field,
  options,
  isViewMode = false,
  displayInColumns = false,
  displayAllOption = true,
}: ControllerCheckboxGroupProps<FieldsType>) => {
  const allChecked = options.every((option) =>
    field.value.includes(option.value),
  );

  const handleAllChange = () => {
    const newValue = allChecked ? [] : options.map((option) => option.value);
    field.onChange(newValue);
  };

  const handleOptionChange = (optionValue: string) => {
    const newValue = field.value.includes(optionValue)
      ? field.value.filter((v: string) => v !== optionValue)
      : [...field.value, optionValue];
    field.onChange(newValue);
  };

  return (
    <div
      className={clsx('flex justify-between flex-col w-full gap-2', {
        'md:flex-row': !displayInColumns,
      })}
    >
      {!isViewMode && displayAllOption && (
        <Label
          className="flex items-center gap-1"
          htmlFor={`all-${field.name}`}
        >
          <Checkbox
            id={`all-${field.name}`}
            checked={allChecked}
            onClick={handleAllChange}
          />
          Tous
        </Label>
      )}
      {options.map((option, index) => (
        <Label
          className={clsx('flex items-center gap-1', {
            'flex-col-reverse': isViewMode && !displayInColumns,
          })}
          key={`option.value-${index}`}
          htmlFor={`option-${option.value}-${field.name}`}
        >
          <Checkbox
            {...field}
            id={`option-${option.value}-${field.name}`}
            checked={field.value.includes(option.value)}
            onClick={() => handleOptionChange(option.value)}
            value={option.value}
            disabled={isViewMode}
          />
          {option.label}
        </Label>
      ))}
    </div>
  );
};

export default ControllerCheckboxGroup;
